import { useState, useCallback, useMemo, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { POST_VERIFY_NEW_ESTIMATE, persistQuickQuoteDraft } from '../lib/postSignIn'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from './ui/button'
import { Card, CardContent, CardHeader } from './ui/card'

// Available plans - moved outside component to prevent re-creation
const PLANS = [
  {
    id: 'free',
    name: 'Free',
    price: 0,
    description: 'Perfect for getting started',
    features: [
      '1 employee included',
      'Up to 20 clients',
      '20 emails + SMS per month',
      '5 AI messages per day',
      'Custom branded Estimates & Invoices',
      'Job & Employee scheduling',
      'Digital property measurement',
      'Standard Stripe card processing fees apply'
    ],
    popular: false,
    stripeProductId: 'free_plan_no_stripe', // No Stripe product needed for free plan
    stripePriceId: 'free_plan_no_stripe' // No Stripe price needed for free plan
  },
  {
    id: 'paid',
    name: 'Paid',
    price: 39.99,
    description: 'Everything you need to grow',
    features: [
      '2 employees included',
      'Unlimited clients',
      '100 emails + SMS per month',
      'Unlimited AI messaging',
      'All Free plan features',
      'Media uploads (before/after photos)',
      'All premium features',
      'Standard Stripe card processing fees apply',
      '+$9.99/month per additional employee'
    ],
    popular: true,
    stripeProductId: 'prod_THjvAChWEmdqbH',
    stripePriceId: 'price_1Ss7VWHBAXwbffJjOuuvtp9K'
  }
]

const SimpleOnboarding = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [step, setStep] = useState('info') // 'info' or 'payment'
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    companyName: ''
  })
  const [selectedPlan, setSelectedPlan] = useState(null)
  const [acceptLegalDocs, setAcceptLegalDocs] = useState(false) // Bundled: Privacy Policy + Terms of Service
  const [acceptNotifications, setAcceptNotifications] = useState(false) // Bundled: SMS + Email notifications
  const [appleLoading, setAppleLoading] = useState(false)

  // Handle pre-selected plan from URL parameters
  useEffect(() => {
    const planId = searchParams.get('plan')
    const entry = searchParams.get('entry')
    
    if (planId && entry === 'subscription') {
      const plan = PLANS.find(p => p.id === planId)
      if (plan) {
        setSelectedPlan(plan)
        // Skip plan selection step if plan is pre-selected
        setStep('info')
      }
    }
  }, [searchParams])

  // Check if user came from free trial button
  const isTrialEntry = searchParams.get('entry') === 'trial'
  // The /quickquote lead magnet: the first sign-in opens the estimate form
  const isQuickQuoteEntry = searchParams.get('quickquote') === 'true'

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    setError(null)
  }, [])

  // Memoize header content to prevent re-renders
  const headerContent = useMemo(() => {
    if (step === 'info') {
      return {
        title: 'Get Started',
        description: selectedPlan 
          ? `Create your account for the ${selectedPlan.name} plan`
          : 'Create your account to start managing your business'
      }
    }
    return {
      title: 'Confirm Your Selection',
      description: isTrialEntry 
        ? 'This is the plan that will start after your 31-day free trial ends. You will be billed for this plan once your trial period is over.'
        : 'Review your selected plan and proceed to payment'
    }
  }, [step, selectedPlan, isTrialEntry])

  // Memoize event handlers to prevent re-renders
  const handlePlanSelect = useCallback((plan) => {
    setSelectedPlan(plan)
  }, [])

  const handleBackToInfo = useCallback(() => {
    setStep('info')
  }, [])

  const handleNavigateToLogin = useCallback(() => {
    // Sign-in is the landing-page modal; there is no /login route.
    navigate('/?auth=login')
  }, [navigate])

  // Memoize plan card className to prevent re-renders
  const getPlanCardClassName = useCallback((plan) => {
    return `cursor-pointer transition-all duration-300 ${
      selectedPlan?.id === plan.id 
        ? 'border-teal-500 bg-teal-500/20 shadow-lg shadow-teal-500/20' 
        : 'border-slate-600/50 hover:border-teal-500/50 hover:bg-overlay-strong'
    } ${plan.popular ? 'ring-2 ring-teal-500/30 shadow-lg shadow-teal-500/10' : ''}`
  }, [selectedPlan])

  // Memoize button text to prevent re-renders
  const buttonText = useMemo(() => {
    if (loading) return 'Processing Payment...'
    return `Pay for ${selectedPlan?.name} Plan`
  }, [loading, selectedPlan])

  // Memoize card styles to prevent re-renders
  const cardStyle = useMemo(() => ({
    backgroundColor: 'rgba(30, 41, 59, 0.6)',
    backdropFilter: 'blur(8px)'
  }), [])


  const handleAppleSignUp = async () => {
    try {
      setAppleLoading(true)
      setError(null)

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      })

      if (error) throw error
    } catch (err) {
      setError(err.message || 'Apple sign up failed')
      setAppleLoading(false)
    }
  }

  const handleInfoSubmit = async (e) => {
    e.preventDefault()
    setError(null)

    // Validate form
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.password || !formData.companyName) {
      setError('Please fill in all fields')
      return
    }

    // Validate legal document acceptance
    if (!acceptLegalDocs) {
      setError('You must accept the Privacy Policy and Terms of Service to continue')
      return
    }
    if (!acceptNotifications) {
      setError('You must consent to SMS and Email notifications to continue')
      return
    }

    // *** NEW: IF FREE PLAN - Create account WITHOUT Stripe ***
    if (selectedPlan?.id === 'free') {
      setLoading(true)
      try {
        console.log('🎯 Free plan signup - creating account without Stripe')

        // Use edge function to create user AND company together
        // This avoids any auth trigger issues by using admin API
        const { data: freeData, error: freeError } = await supabase.functions.invoke('create-trial-account', {
          body: {
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
            companyName: formData.companyName,
            planType: 'free', // Specify this is a free plan, not a trial
            ...(isQuickQuoteEntry && { postVerifyRedirect: POST_VERIFY_NEW_ESTIMATE }),
            legalAcceptance: {
              privacyAccepted: acceptLegalDocs,
              eulaAccepted: acceptLegalDocs,
              acceptedAt: new Date().toISOString()
            },
            notificationConsent: {
              smsConsent: acceptNotifications,
              emailConsent: acceptNotifications
            }
          }
        })

        if (freeError) throw freeError

        console.log('✅ Free plan account created:', freeData)

        // The account cannot sign in until its email is verified (#428), so
        // send the user to sign-in with instructions instead of logging in.
        if (isQuickQuoteEntry) persistQuickQuoteDraft()
        navigate(`/?auth=login&check_email=true&email=${encodeURIComponent(formData.email.trim().toLowerCase())}`)

      } catch (err) {
        console.error('❌ Free plan signup error:', err)
        setError(err.message || 'Failed to create free account. Please try again.')
      } finally {
        setLoading(false)
      }
      return
    }

    // *** IF TRIAL ENTRY - Create account WITHOUT Stripe ***
    if (isTrialEntry) {
      setLoading(true)
      try {
        console.log('🎯 Free trial signup - creating account without Stripe')

        // Use edge function to create user AND company together
        // This avoids any auth trigger issues by using admin API
        const { data: trialData, error: trialError } = await supabase.functions.invoke('create-trial-account', {
          body: {
            email: formData.email,
            password: formData.password,
            firstName: formData.firstName,
            lastName: formData.lastName,
            companyName: formData.companyName,
            trialDays: 31,
            ...(isQuickQuoteEntry && { postVerifyRedirect: POST_VERIFY_NEW_ESTIMATE }),
            legalAcceptance: {
              privacyAccepted: acceptLegalDocs,
              eulaAccepted: acceptLegalDocs,
              acceptedAt: new Date().toISOString()
            },
            notificationConsent: {
              smsConsent: acceptNotifications,
              emailConsent: acceptNotifications
            }
          }
        })

        if (trialError) throw trialError

        console.log('✅ Trial account created:', trialData)

        // The account cannot sign in until its email is verified (#428), so
        // send the user to sign-in with instructions instead of logging in.
        if (isQuickQuoteEntry) persistQuickQuoteDraft()
        navigate(`/?auth=login&check_email=true&email=${encodeURIComponent(formData.email.trim().toLowerCase())}`)

      } catch (err) {
        console.error('❌ Trial signup error:', err)
        setError(err.message || 'Failed to create trial account. Please try again.')
      } finally {
        setLoading(false)
      }
      return
    }

    // ELSE - Regular paid signup flow (existing code)
    // If plan is pre-selected, go directly to payment
    if (selectedPlan) {
      setStep('payment')
    } else {
      // If no plan selected, go to plan selection
      setStep('payment')
    }
  }

  const handlePaymentSubmit = async () => {
    if (!selectedPlan) {
      setError('Please select a plan')
      return
    }

    setLoading(true)
    setError(null)

    try {
      // Prepare request body
      const requestBody = {
        userEmail: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        companyName: formData.companyName,
        planId: selectedPlan.id,
        planName: selectedPlan.name,
        planPrice: Math.round(selectedPlan.price * 100), // Convert to cents for database storage
        stripeProductId: selectedPlan.stripeProductId,
        stripePriceId: selectedPlan.stripePriceId,
        successUrl: `${window.location.origin}/email-verification?payment=success`,
        cancelUrl: `${window.location.origin}/onboarding?step=payment`,
        // Only pass trialDays if user came from free trial button
        ...(isTrialEntry && { trialDays: 31 }),
        // Store user data to be created after payment
        userData: {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          companyName: formData.companyName,
          selectedPlan: selectedPlan.id,
          // Legal document acceptance data (both accepted when bundled checkbox is checked)
          legalAcceptance: {
            privacyAccepted: acceptLegalDocs,
            eulaAccepted: acceptLegalDocs,
            acceptedAt: new Date().toISOString()
          },
          // Notification consent data (both accepted when bundled checkbox is checked)
          notificationConsent: {
            smsConsent: acceptNotifications,
            emailConsent: acceptNotifications
          }
        }
      }

      // Log trial information for verification
      console.log('🚀 Creating checkout session with:', {
        isTrialEntry,
        trialDays: isTrialEntry ? 31 : 'not included',
        planId: selectedPlan.id,
        planName: selectedPlan.name
      })

      // Create Stripe checkout session for pending subscription (without creating user account yet)
      const { data, error } = await supabase.functions.invoke('create-pending-checkout-session', {
        body: requestBody
      })

      if (error) {
        throw new Error(error.message || 'Failed to create checkout session')
      }

      const { url, pendingSubscriptionId } = data

      // Log response for verification
      console.log('✅ Checkout session created:', {
        sessionId: data.sessionId,
        pendingSubscriptionId,
        isTrialEntry,
        trialDays: isTrialEntry ? 31 : 'none',
        hasTrial: data.hasTrial,
        trialEndDate: data.trialEndDate
      })
      
      // Verify trial was applied
      if (isTrialEntry) {
        if (data.hasTrial && data.trialDays === 31) {
          console.log('✅ VERIFIED: Trial period (31 days) successfully applied to checkout session')
          console.log('📅 Trial will end on:', data.trialEndDate ? new Date(data.trialEndDate).toLocaleString() : 'Date not available')
        } else {
          console.warn('⚠️ WARNING: Trial period may not have been applied correctly', {
            expected: { hasTrial: true, trialDays: 31 },
            received: { hasTrial: data.hasTrial, trialDays: data.trialDays }
          })
        }
      }

      // Store pending subscription info in session storage
      sessionStorage.setItem('pending_subscription', JSON.stringify({
        id: pendingSubscriptionId,
        plan: selectedPlan,
        userEmail: formData.email,
        companyName: formData.companyName,
        isTrialEntry,
        trialDays: isTrialEntry ? 31 : null
      }))
      
      // The auth user created by create-pending-checkout-session stays
      // unconfirmed until the verification email sent after payment is
      // clicked (#428), so there is no session to carry through Stripe.

      // Track InitiateCheckout event for Meta Pixel
      try {
        const { trackMetaPixelEvent } = await import('../lib/metaPixel')
        trackMetaPixelEvent('InitiateCheckout', {
          content_name: selectedPlan.name,
          content_category: 'Subscription',
          content_ids: [selectedPlan.id],
          value: selectedPlan.price,
          currency: 'USD'
        })
      } catch (err) {
        // Meta Pixel import failed, continue without tracking
        console.warn('Failed to track Meta Pixel InitiateCheckout:', err)
      }

      // Redirect to Stripe checkout
      window.location.href = url
      
    } catch (error) {
      console.error('Payment setup error:', error)
      setError(error.message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          <Card className="bg-elevated/60 border-2 border-slate-700/50 shadow-xl" style={cardStyle}>
            <CardHeader className="text-center pb-6">
              <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500/20 to-cyan-500/20 backdrop-blur-sm border border-teal-500/30 mb-4">
                <img 
                  src="/icons/logo_1024x1024-noBGm.png" 
                  alt="Prosperia CRM Logo" 
                  className="w-10 h-10 object-contain"
                />
              </div>
              
              <h1 className="text-3xl font-bold text-white mb-2">
                {headerContent.title}
              </h1>
              
              <p className="text-secondary">
                {headerContent.description}
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Error Message */}
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div 
                    className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg backdrop-blur-sm"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.3 }}
                  >
                    <p className="text-red-400 text-sm">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Step 1: Basic Info */}
              <AnimatePresence mode="wait">
                {step === 'info' && (
                  <motion.form 
                    key="info-form"
                    onSubmit={handleInfoSubmit} 
                    className="space-y-4"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ duration: 0.3 }}
                  >
                  {/* Name Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.firstName}
                        onChange={(e) => handleInputChange('firstName', e.target.value)}
                        className="onboarding-form-input w-full px-4 py-3 border border-slate-600/50 rounded-lg text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                        placeholder="John"
                        style={{ 
                          backgroundColor: 'rgba(51, 65, 85, 0.8)',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          transform: 'translateZ(0)'
                        }}
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-secondary mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.lastName}
                        onChange={(e) => handleInputChange('lastName', e.target.value)}
                        className="onboarding-form-input w-full px-4 py-3 border border-slate-600/50 rounded-lg text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                        placeholder="Doe"
                        style={{ 
                          backgroundColor: 'rgba(51, 65, 85, 0.8)',
                          WebkitAppearance: 'none',
                          MozAppearance: 'none',
                          transform: 'translateZ(0)',
                          WebkitBackfaceVisibility: 'hidden',
                          backfaceVisibility: 'hidden',
                          isolation: 'isolate',
                          contain: 'layout style paint'
                        }}
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="onboarding-form-input w-full px-4 py-3 border border-slate-600/50 rounded-lg text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                      placeholder="john@company.com"
                      style={{ 
                        backgroundColor: 'rgba(51, 65, 85, 0.8)',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        transform: 'translateZ(0)'
                      }}
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Password
                    </label>
                    <input
                      type="password"
                      required
                      minLength={6}
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      className="onboarding-form-input w-full px-4 py-3 border border-slate-600/50 rounded-lg text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                      placeholder="••••••••"
                      style={{ 
                        backgroundColor: 'rgba(51, 65, 85, 0.8)',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        transform: 'translateZ(0)'
                      }}
                    />
                  </div>

                  {/* Company Name */}
                  <div>
                    <label className="block text-sm font-medium text-secondary mb-2">
                      Company Name
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.companyName}
                      onChange={(e) => handleInputChange('companyName', e.target.value)}
                      className="onboarding-form-input w-full px-4 py-3 border border-slate-600/50 rounded-lg text-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none"
                      placeholder="Your Company"
                      style={{ 
                        backgroundColor: 'rgba(51, 65, 85, 0.8)',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        transform: 'translateZ(0)'
                      }}
                    />
                  </div>

                  {/* Legal Document Acceptance - Bundled */}
                  <div className="space-y-3 pt-4 border-t border-slate-600/50">
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="acceptLegalDocs"
                        checked={acceptLegalDocs}
                        onChange={(e) => setAcceptLegalDocs(e.target.checked)}
                        className="mt-1 h-4 w-4 text-teal-500 focus:ring-teal-500 border-mute rounded bg-elevated/50"
                        required
                      />
                      <label htmlFor="acceptLegalDocs" className="ml-3 text-sm text-secondary">
                        I have read and accept the{' '}
                        <Link 
                          to="/privacy" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-teal-400 hover:text-teal-300 underline font-medium"
                        >
                          Privacy Policy
                        </Link>
                        {' '}and{' '}
                        <Link 
                          to="/eula" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-teal-400 hover:text-teal-300 underline font-medium"
                        >
                          Terms of Service (EULA)
                        </Link>
                        {' '}*
                      </label>
                    </div>

                    {/* Notification Consent - Bundled */}
                    <div className="flex items-start">
                      <input
                        type="checkbox"
                        id="acceptNotifications"
                        checked={acceptNotifications}
                        onChange={(e) => setAcceptNotifications(e.target.checked)}
                        className="mt-1 h-4 w-4 text-teal-500 focus:ring-teal-500 border-mute rounded bg-elevated/50"
                        required
                      />
                      <label htmlFor="acceptNotifications" className="ml-3 text-sm text-secondary">
                        I consent to receive SMS and Email notifications *
                      </label>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div>
                    <Button
                      type="submit"
                      disabled={!acceptLegalDocs || !acceptNotifications}
                      className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-lg focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-slate-800 transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-teal-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Continue
                    </Button>
                  </div>

                  {/* Divider */}
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-mute"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-3 bg-elevated text-muted">or</span>
                    </div>
                  </div>

                  {/* Apple Sign Up */}
                  <div>
                    <button
                      type="button"
                      onClick={handleAppleSignUp}
                      disabled={loading || appleLoading}
                      className="w-full flex items-center justify-center gap-3 bg-white hover:bg-hover text-black font-semibold py-3 px-4 rounded-lg shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      tabIndex={0}
                      aria-label="Sign up with Apple"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleAppleSignUp() }}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                      </svg>
                      {appleLoading ? 'Connecting...' : 'Sign up with Apple'}
                    </button>
                  </div>

                  <p className="text-xs text-muted text-center mt-2">
                    By continuing with Apple, you agree to our{' '}
                    <Link to="/privacy" className="text-teal-400 hover:text-teal-300">Privacy Policy</Link>
                    {' '}and{' '}
                    <Link to="/terms" className="text-teal-400 hover:text-teal-300">Terms of Service</Link>
                  </p>
                </motion.form>
              )}
              </AnimatePresence>

              {/* Step 2: Payment */}
              <AnimatePresence mode="wait">
                {step === 'payment' && (
                  <motion.div 
                    key="payment-step" 
                    className="space-y-4"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    {/* Trial Notice Banner */}
                    {isTrialEntry && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="p-4 bg-gradient-to-r from-teal-500/20 to-cyan-500/20 border-2 border-teal-500/50 rounded-lg backdrop-blur-sm"
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0">
                            <i className="fas fa-gift text-teal-400 text-xl mr-3"></i>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-white font-semibold mb-1">31-Day Free Trial Included</h4>
                            <p className="text-secondary text-sm">
                              You&apos;ll start with a 31-day free trial. After the trial ends, you&apos;ll be billed {selectedPlan 
                                ? `$${selectedPlan.price}/month for the ${selectedPlan.name} plan`
                                : 'for the plan'
                              }. No charges during the trial period.
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    {/* Show selected plan if pre-selected, otherwise show plan selection */}
                    {selectedPlan ? (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Card className="bg-elevated/60 border-2 border-teal-500/50 backdrop-blur-sm">
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="text-xl font-bold text-white mb-1">
                                  {selectedPlan.name}
                                  {selectedPlan.popular && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-teal-500 text-white rounded-full">
                                      Most Popular
                                    </span>
                                  )}
                                </h3>
                                <p className="text-secondary text-sm">{selectedPlan.description}</p>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-white">
                                  ${selectedPlan.price}
                                  <span className="text-sm text-muted">/month</span>
                                </div>
                                {isTrialEntry && (
                                  <div className="text-xs text-teal-400 mt-1">
                                    After 31-day trial
                                  </div>
                                )}
                              </div>
                            </div>
                            
                            <ul className="space-y-2 mb-4">
                              {selectedPlan.features.map((feature, featureIndex) => (
                                <li key={featureIndex} className="flex items-center text-secondary text-sm">
                                  <i className="fas fa-check text-teal-400 mr-2"></i>
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ) : (
                      // Show plan selection if no plan pre-selected
                      PLANS.map((plan, index) => (
                        <motion.div
                          key={plan.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          layout={false}
                        >
                        <Card 
                          className={`${getPlanCardClassName(plan)} bg-elevated/60 border-2 backdrop-blur-sm`}
                          onClick={() => handlePlanSelect(plan)}
                        >
                          <CardContent className="p-6">
                            <div className="flex justify-between items-start mb-4">
                              <div>
                                <h3 className="text-xl font-bold text-white mb-1">
                                  {plan.name}
                                  {plan.popular && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-teal-500 text-white rounded-full">
                                      Most Popular
                                    </span>
                                  )}
                                </h3>
                                <p className="text-secondary text-sm">{plan.description}</p>
                              </div>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-white">
                                  ${plan.price}
                                  <span className="text-sm text-muted">/month</span>
                                </div>
                              </div>
                            </div>
                            
                            <ul className="space-y-2 mb-4">
                              {plan.features.map((feature, featureIndex) => (
                                <li key={featureIndex} className="flex items-center text-secondary text-sm">
                                  <i className="fas fa-check text-teal-400 mr-2"></i>
                                  {feature}
                                </li>
                              ))}
                            </ul>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))
                    )}

                    {/* Payment Buttons */}
                    <div className="space-y-3">
                      <Button
                        onClick={handlePaymentSubmit}
                        disabled={loading || !selectedPlan}
                        className="w-full px-6 py-3 bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold rounded-lg focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-teal-500/25"
                      >
                        {buttonText}
                      </Button>
                      
                      <button
                        onClick={handleBackToInfo}
                        className="w-full text-muted hover:text-white transition-colors duration-300"
                      >
                        ← Back to Account Info
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Footer */}
              <div className="text-center pt-4">
                <p className="text-sm text-muted">
                  Already have an account?{' '}
                  <button
                    onClick={handleNavigateToLogin}
                    className="text-teal-400 hover:text-teal-300 font-medium transition-colors duration-300"
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
}

export default SimpleOnboarding
